// @ts-nocheck
import { useState, useEffect, useRef, MouseEvent, useMemo } from 'react';
import {
  CloseOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import classnames from 'classnames';
import { getImageOriginalUrl, getThumbUrl } from '../../utils/dataUtils';
import { RenderDOMFunc } from '../../constants/interface';
import './style.less';

interface IMoveData {
  clientX: number;
  clientY: number;
}
const INIT_DATA: IMoveData = {
  clientX: 0,
  clientY: 0,
};
interface ImagesData {
  loading: boolean;
  blob?: any;
}
export interface PreviewImageProps {
  /** 菜单渲染父节点。默认渲染到 body 上 */
  getPopupContainer?: RenderDOMFunc;
  /** 当前选中的图片路径 */
  current?: string;
  /** 图片路径组 */
  images?: string[];
  /** 图片切换时调用 */
  onChange?: (image?: string, images?: string[]) => void;
  /** 关闭时调用 */
  onClose?: () => void;
}
export const PreviewImage = (props: PreviewImageProps) => {
  const { onClose, onChange, current, images = [] } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const moveData = useRef<IMoveData>(INIT_DATA);
  const prveMoveData = useRef<IMoveData>(INIT_DATA);
  const progressRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  /** 是否显示 */
  const [visible, setVisible] = useState<boolean>(true);
  /** 图片加载数据集合 */
  const [imagesInfo, setImagesInfo] = useState<ImagesData[]>(
    images.map(() => ({ loading: true })),
  );
  /** 当前鼠标是否按下 */
  const [move, setMove] = useState<boolean>(false);
  /** 当前显示第几张图片 */
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  /** 放大倍数 */
  const [multiple, setMultiple] = useState<number>(1);
  /** 旋转次数 */
  const [rotate, setRotate] = useState<number>(0);
  useEffect(() => {
    if (!visible) setVisible(true);
  }, [props]);
  useEffect(() => {
    const handleWheel = function(e: WheelEvent) {
      let newMultiple = multiple;
      if (e.deltaY > 0) {
        newMultiple = newMultiple - 1;
      } else {
        newMultiple = newMultiple + 1;
      }
      handleChangeSize(newMultiple);
    };
    document.addEventListener('wheel', handleWheel, false);
    return () => {
      document.removeEventListener('wheel', handleWheel);
    };
  }, [multiple]);
  useEffect(() => {
    let newIndex = 0;
    if (current && images) {
      if (typeof current === 'number') {
        newIndex === current;
      } else {
        newIndex = images.findIndex(v => v === current);
      }
    }
    setCurrentIndex(newIndex);
  }, [current, images]);
  useEffect(() => {
    const currentImageSrc = images[currentIndex];
    if (!currentImageSrc || !imagesInfo[currentIndex].loading) return;
    const req = new XMLHttpRequest();
    req.open('get', getImageOriginalUrl(currentImageSrc), true);
    req.responseType = 'blob';
    req.send();
    /** 请求发生错误是用原图路径加载 */
    req.addEventListener('error', () => {
      setImagesInfo(
        imagesInfo.map((item, index) => {
          if (index === currentIndex) {
            return {
              loading: false,
              blob: getImageOriginalUrl(currentImageSrc),
            };
          }
          return item;
        }),
      );
    });
    req.addEventListener('progress', oEvent => {
      const progressElement = progressRef.current;
      if (oEvent.lengthComputable && progressElement) {
        const percentComplete = Math.floor(
          (oEvent.loaded / oEvent.total) * 100,
        );
        progressElement.innerHTML = `${percentComplete}%`;
      }
    });
    req.addEventListener('load', () => {
      const blob = req.response;
      setImagesInfo(
        imagesInfo.map((item, index) => {
          if (index === currentIndex) {
            return { loading: false, blob: window.URL.createObjectURL(blob) };
          }
          return item;
        }),
      );
      const progressElement = progressRef.current;
      if (progressElement) progressElement.innerHTML = '';
    });
    return () => {
      req.abort();
    };
  }, [currentIndex, images]);
  /** 图片切换 */
  const handleChangeIndex = (index: number) => {
    if (index < 0) return;
    if (index > images.length - 1) return;
    setMultiple(1);
    setRotate(0);
    resetImageShift();
    setCurrentIndex(index);
    onChange && onChange(images[index], images);
  };
  /** 旋转角度改变 */
  const handleChangeRotate = (index: number) => {
    // if (index === -4) index = 0;
    // if (index === 4) index = 0;
    setRotate(index);
  };
  /** 放大倍数改变 */
  const handleChangeSize = (index: number) => {
    if (index < 1) return;
    if (index > 10) return;
    resetImageShift();
    setMultiple(index);
  };
  /** 关闭 */
  const handleClose = () => {
    setVisible(false);
    onClose && onClose();
  };
  /** 鼠标按下 */
  const handleMouseDown = (e: MouseEvent<HTMLElement>) => {
    const moveTarget = e.target as HTMLElement;
    if (moveTarget?.classList?.contains?.('yb-preview-image-img')) {
      moveData.current = {
        clientX: e.clientX,
        clientY: e.clientY,
      };
      setMove(true);
    }
    e.preventDefault();
  };
  /** 鼠标移动 */
  const handleMouseMove = (e: MouseEvent<HTMLImageElement>) => {
    if (!move || !divRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const currentClientX = e.clientX;
    const currentClientY = e.clientY;
    const difX = currentClientX - moveData.current.clientX;
    const difY = currentClientY - moveData.current.clientY;
    divRef.current.style.transform = `translate3d(${prveMoveData.current
      .clientX + difX}px, ${prveMoveData.current.clientY + difY}px, 0px)`;
    return false;
  };
  /** 鼠标抬起 */
  const handleMouseUp = (e: MouseEvent<HTMLImageElement>) => {
    setMove(false);
    e.preventDefault();
    const imageElement = imageRef.current;
    if (!imageElement || !divRef.current) return resetImageShift();
    const imageWidth = imageElement.width * multiple;
    const imageHeight = imageElement.height * multiple;
    const wrapperWidth = document.body.clientWidth;
    const wrapperHeight = document.body.clientHeight;
    if (imageWidth < wrapperWidth && imageHeight < wrapperHeight) {
      return resetImageShift();
    }
    const currentClientX = e.clientX;
    const currentClientY = e.clientY;
    const difX =
      prveMoveData.current.clientX + currentClientX - moveData.current.clientX;
    const difY =
      prveMoveData.current.clientY + currentClientY - moveData.current.clientY;
    const maxDifX =
      imageWidth > wrapperWidth ? (imageWidth - wrapperWidth) / 2 : 0;
    const maxDifY =
      imageHeight > wrapperHeight ? (imageHeight - wrapperHeight) / 2 : 0;
    const minDifX =
      imageWidth > wrapperWidth ? (wrapperWidth - imageWidth) / 2 : 0;
    const minDifY =
      imageHeight > wrapperHeight ? (wrapperHeight - imageHeight) / 2 : 0;
    prveMoveData.current = {
      clientX: difX > maxDifX ? maxDifX : difX < minDifX ? minDifX : difX,
      clientY: difY > maxDifY ? maxDifY : difY < minDifY ? minDifY : difY,
    };
    divRef.current.style.transform = `translate3d(${prveMoveData.current.clientX}px, ${prveMoveData.current.clientY}px, 0px)`;
  };
  /** 鼠标移除屏幕 */
  // const handleMouseLeave = () => {
  //   setMove(false);
  //   resetImageShift();
  // };
  /** 初始化位移 */
  const resetImageShift = () => {
    if (divRef.current) {
      divRef.current.style.transform = `translate3d(0px, 0px, 0px)`;
    }
    prveMoveData.current = INIT_DATA;
  };
  const zoomOutOutClasses = useMemo(() => {
    return classnames('yb-preview-image-header-icon', {
      disabled: multiple <= 1,
    });
  }, [multiple]);
  const zoomInOutClasses = useMemo(() => {
    return classnames('yb-preview-image-header-icon', {
      disabled: multiple === 10,
    });
  }, [multiple]);
  const leftBtnClasses = useMemo(() => {
    return classnames('yb-preview-image-body-operation operation-left', {
      disabled: currentIndex === 0,
    });
  }, [currentIndex]);
  const rightBtnClasses = useMemo(() => {
    return classnames('yb-preview-image-body-operation operation-right', {
      disabled: currentIndex === images.length - 1,
    });
  }, [currentIndex]);
  const bodyClasses = useMemo(() => {
    return classnames('yb-preview-image-body', {
      'yb-preview-image-body-move': move,
    });
  }, [move]);
  const wrapperClasses = useMemo(() => {
    return classnames('yb-preview-image-wrapper', {
      hidden: !visible,
    });
  }, [visible]);
  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      // onMouseLeave={handleMouseUp}
    >
      {visible && <div className="yb-preview-image-mask"></div>}
      <div className={wrapperClasses}>
        <div className="yb-preview-image">
          <div
            className="yb-preview-image-header"
            style={{ display: visible ? 'flex' : 'none' }}
          >
            <div
              className="yb-preview-image-header-item"
              onClick={() => handleChangeRotate(rotate - 1)}
            >
              <RotateLeftOutlined className="yb-preview-image-header-icon" />
            </div>
            <div
              className="yb-preview-image-header-item"
              onClick={() => handleChangeRotate(rotate + 1)}
            >
              <RotateRightOutlined className="yb-preview-image-header-icon" />
            </div>
            <div
              className="yb-preview-image-header-item"
              onClick={() => handleChangeSize(multiple - 1)}
            >
              <ZoomOutOutlined className={zoomOutOutClasses} />
            </div>
            <div
              className="yb-preview-image-header-item"
              onClick={() => handleChangeSize(multiple + 1)}
            >
              <ZoomInOutlined className={zoomInOutClasses} />
            </div>
            <div className="yb-preview-image-header-item" onClick={handleClose}>
              <CloseOutlined className="yb-preview-image-header-icon" />
            </div>
          </div>
          <div className={bodyClasses} onClick={handleClose}>
            {images.length > 1 && (
              <>
                <div
                  className={leftBtnClasses}
                  onClick={e => {
                    e.stopPropagation();
                    handleChangeIndex(currentIndex - 1);
                  }}
                >
                  <LeftOutlined />
                </div>
                <div
                  className={rightBtnClasses}
                  onClick={e => {
                    e.stopPropagation();
                    handleChangeIndex(currentIndex + 1);
                  }}
                >
                  <RightOutlined />
                </div>
              </>
            )}
            <div
              ref={divRef}
              className="yb-preview-image-body-content"
              style={{ transform: `translate3d(0px, 0px, 0px)` }}
              onClick={e => e.stopPropagation()}
            >
              {images && (
                <>
                  {
                    <div
                      style={{
                        display: imagesInfo[currentIndex].loading
                          ? 'block'
                          : 'none',
                      }}
                    >
                      <img
                        className="yb-preview-image-img"
                        src={getThumbUrl(images[currentIndex])}
                      />
                      <div className="yb-preview-image-img-loading">
                        <div
                          ref={progressRef}
                          className="yb-preview-image-img-progress"
                        ></div>
                        <LoadingOutlined
                          style={{ color: 'white', fontSize: 24 }}
                        />
                      </div>
                    </div>
                  }
                  {!!imagesInfo[currentIndex].blob && (
                    <img
                      ref={imageRef}
                      className="yb-preview-image-img"
                      src={imagesInfo[currentIndex].blob}
                      style={{
                        transform: `scale3d(${multiple}, ${multiple}, 1) rotate(${rotate *
                          90}deg)`,
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewImage;
